import { useEffect, useState } from 'react';
import {
  dismissUploadError,
  hasPendingUploads,
  subscribeToUploads,
  type QueueState,
} from './uploadQueue';
import { IconClose, Spinner } from './ui';

/**
 * The only thing an upload puts on screen. Sits above the tab bar so it is
 * visible from any tab without taking a screen over, which is the point of
 * moving uploads to the background in the first place.
 */
export function UploadStatus() {
  const [queue, setQueue] = useState<QueueState>({
    active: null,
    queued: 0,
    error: null,
    resumed: 0,
  });

  useEffect(() => subscribeToUploads(setQueue), []);

  // Browsers only honour this while the tab is in the foreground, and an
  // installed iOS PWA mostly ignores it — but where it does work it saves a
  // photo from waiting for the next launch.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!hasPendingUploads()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  if (!queue.active && !queue.error) return null;

  const failed = Boolean(queue.error);

  return (
    <div
      role="status"
      className="row"
      /*
       * Above the camera key, and clear of where it sits.
       *
       * The key climbs out of the tab bar — 24px on most tabs, 92 on the
       * camera one — and the bar is painted after this, so it landed on top of
       * whatever this was saying. An upload failure would report its reason
       * and have the reason covered by a button, which is how "it will retry"
       * arrived with the half of the sentence that mattered missing.
       */
      style={{
        flex: '0 0 auto',
        gap: 10,
        margin: '0 var(--gutter) 30px',
        padding: '10px 12px',
        borderRadius: 'var(--r-sm)',
        background: failed ? 'var(--danger-tint)' : 'var(--surface)',
        border: `1px solid ${failed ? 'var(--danger)' : 'var(--line)'}`,
        position: 'relative',
        zIndex: 3,
      }}
    >
      {failed ? (
        <>
          <span className="grow small" style={{ color: 'var(--danger)' }}>
            {queue.error}
          </span>
          <button
            className="icon-btn"
            aria-label="Dismiss"
            onClick={dismissUploadError}
            style={{ width: 32, height: 32, background: 'none' }}
          >
            <IconClose size={16} />
          </button>
        </>
      ) : (
        <>
          {queue.active?.picture ? <SendingThumb picture={queue.active.picture} /> : <Spinner />}
          <span className="grow small">
            {queue.resumed > 0 ? 'Finishing ' : 'Posting '}
            {queue.active?.label}
            {queue.queued > 0 ? ` (+${queue.queued} waiting)` : ''}
          </span>
          <span className="small bold" style={{ color: 'var(--accent)' }}>
            {Math.round((queue.active?.progress ?? 0) * 100)}%
          </span>
        </>
      )}
    </div>
  );
}

/** The photo going up, small, in place of the spinner: which one it is, at a glance. */
function SendingThumb({ picture }: { picture: Blob }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const made = URL.createObjectURL(picture);
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [picture]);
  if (!url) return null;
  return <img className="sending-thumb" src={url} alt="" />;
}
